import api from './api';

// AI Insights (MOD_11_INSIGHT). Everything here is scoped server-side to the
// ACTING role, so what comes back depends on which modules that role holds —
// there is nothing to filter on this side, and nothing that should be.
export const insightsService = {
  // Models the selector may offer: active provider, active model, key stored.
  // When `models` is empty, `reason` explains which of those is missing so the
  // page can send an administrator to the right screen instead of showing an
  // empty dropdown.
  getModels: async () => {
    const res = await api.get('/insights/models');
    return res.data;
  },

  // The catalogue as structured data — what the AI knows about this tenant.
  getCatalogue: async () => {
    const res = await api.get('/insights/catalogue');
    return res.data;
  },

  // The exact text that would be prepended to the first turn, and its token
  // size. Pass a model code to have it trimmed to that model's context window.
  getBriefing: async (modelCode) => {
    const res = await api.get('/insights/briefing', {
      params: modelCode ? { model: modelCode } : {},
    });
    return res.data;
  },

  // Ask, or continue a thread. A FAILURE STILL CARRIES A USEFUL BODY — the kind,
  // the provider's own words, the rejected plan — so the catch re-surfaces the
  // payload rather than letting axios turn it into a bare exception.
  // `sqlMode` asks the server to let the model write SQL instead of a checked
  // query plan. Refused with 403 unless the acting role holds the direct-SQL
  // module AND a dedicated read-only database login is configured — both are
  // checked server-side, so this flag is a request, not a permission.
  ask: async (question, modelCode, conversationId, sqlMode = false) => {
    try {
      const res = await api.post('/insights/ask', {
        question,
        model_code: modelCode,
        sql_mode: sqlMode || undefined,
        // Omitted on the first question of a thread; the server creates the
        // conversation and returns its id, so the client never invents one.
        conversation_id: conversationId || undefined,
      });
      return res.data;
    } catch (err) {
      const body = err.response?.data;
      if (body) return { ...body, status: err.response.status };
      throw err;
    }
  },

  // Ask, with the steps arriving as they happen.
  //
  // fetch RATHER THAN EventSource, and rather than axios. EventSource cannot
  // POST or set headers, and every request here carries the JWT and x-db-name.
  // axios has no streaming response in the browser — it buffers the whole body —
  // which is precisely what this call exists to avoid.
  //
  // Resolves with the same payload `ask` returns, so the caller's success path is
  // identical; `onStep` is the only addition.
  //
  // FALLS BACK TO THE PLAIN ENDPOINT if streaming is unavailable — an old
  // browser, or a proxy that refuses the content type. Losing live progress is a
  // worse experience; losing the answer would be a broken feature.
  askStream: async (question, modelCode, conversationId, sqlMode, onStep) => {
    const { API_URL, TENANT_DB } = window.MANTLE_CONFIG || {};
    const url = `${API_URL || 'http://localhost:5000/api'}/insights/ask/stream`;

    const headers = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'x-db-name': TENANT_DB,
    };
    const jwt = localStorage.getItem('jwt');
    if (jwt) headers.Authorization = `Bearer ${jwt}`;
    for (const [k, v] of [
      ['x-role-urid', localStorage.getItem('roleUrid')],
      ['x-site-code', localStorage.getItem('siteCode')],
      ['x-budget-version-id', localStorage.getItem('budgetVersionId')],
    ]) if (v) headers[k] = v;

    let res;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          question,
          model_code: modelCode,
          conversation_id: conversationId || undefined,
          sql_mode: sqlMode || undefined,
        }),
      });
    } catch {
      return insightsService.ask(question, modelCode, conversationId, sqlMode);
    }

    if (!res.ok || !res.body) {
      // A 4xx here is a real refusal (no SQL grant, no version chosen) and its
      // body is JSON, not a stream.
      try {
        const body = await res.json();
        return { ...body, status: res.status };
      } catch {
        return insightsService.ask(question, modelCode, conversationId, sqlMode);
      }
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result = null;
    let failure = null;

    // SSE frames are separated by a blank line. Events can and do arrive split
    // across chunks, so the buffer is only consumed up to the last complete one.
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let split;
      while ((split = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);
        if (frame.startsWith(':')) continue;          // comment / keep-alive

        let event = 'message';
        const data = [];
        for (const line of frame.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) data.push(line.slice(5).trim());
        }
        if (!data.length) continue;

        let payload;
        try { payload = JSON.parse(data.join('\n')); } catch { continue; }

        if (event === 'step') onStep?.(payload);
        else if (event === 'result') result = payload;
        else if (event === 'error') failure = payload;
      }
    }

    if (result) return { success: true, data: result };
    if (failure) return failure;
    // The stream ended without either. Do not guess — the question may well have
    // run and been logged, so say so rather than silently retrying and paying
    // for it twice.
    return { success: false, message: 'The connection closed before an answer arrived. The question may still have been charged — check the conversation before asking again.' };
  },

  // Recent threads for the sidebar. Own conversations only, enforced server-side.
  listConversations: async () => {
    const res = await api.get('/insights/conversations');
    return res.data;
  },

  // Reopening RE-RUNS each stored query rather than replaying stored figures, so
  // the numbers are current and permissions are re-checked. A message that can no
  // longer be replayed comes back with `replay_error` and its prose intact.
  getConversation: async (id) => {
    const res = await api.get(`/insights/conversations/${encodeURIComponent(id)}`);
    return res.data;
  },

  deleteConversation: async (id) => {
    const res = await api.delete(`/insights/conversations/${encodeURIComponent(id)}`);
    return res.data;
  },
};

export default insightsService;
